import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { offsetLimitPagination } from '@apollo/client/utilities';

// Load Apollo Client dev messages only in a dev environment
if (process.env.NODE_ENV !== 'production') {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

async function hash(query: string): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(query));

  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

// Create an error store to capture errors
window.errorStore = [];

// Modify the errorLink to capture and store errors
const errorLink = onError((e) => {
  // ...existing error handling...
  if (e.graphQLErrors && e.graphQLErrors[0].extensions?.code === 'PERSISTED_QUERY_NOT_FOUND') {
    console.info(
      `%cPersisted query not found, falling back to normal query: ${e.operation.operationName}`,
      'color:orange',
    );
  } else {
    console.error(e);

    // Capture GraphQL errors
    if (e.graphQLErrors) {
      e.graphQLErrors.forEach((err) => {
        window.errorStore.push({
          message: `[GraphQL error]: Message: ${err.message}, Location: ${err.locations}, Path: ${err.path}`,
          time: new Date(),
        });
      });
    }
    // Capture network errors
    if (e.networkError) {
      window.errorStore.push({
        message: `[Network error]: ${e.networkError.message}`,
        time: new Date(),
      });
    }

    // After pushing the new error into errorStore
    if (window.errorStore.length > 15) {
      window.errorStore.shift(); // removes the oldest error
    }
  }
});

export const cache = new InMemoryCache({
  typePolicies: {
    Office: {
      keyFields: ['OfficeID'],
    },
    AgentRun: {
      keyFields: ['runId'],
    },
    Person: {
      keyFields: ['id'],
      fields: {
        disciplines: {
          merge: (_, incoming) => incoming,
        },
      },
    },
    Event: {
      keyFields: ['eventID'],
    },
    Query: {
      fields: {
        projectListV2: offsetLimitPagination((args) =>
          Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
        ),
        projectsPaginated: {
          keyArgs: (args) => Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
          merge(existing, incoming, { args }) {
            const merged = existing?.data ? existing.data.slice(0) : [];
            const offset = (args?.offset as number) ?? 0;
            const incomingData = incoming?.data ?? [];

            for (let i = 0; i < incomingData.length; i++) {
              merged[offset + i] = incomingData[i];
            }

            return { ...incoming, data: merged };
          },
        },
        contractProcesses: {
          keyArgs: (args) => Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
          merge(existing, incoming, { args }) {
            // Only the global pipeline scrolls infinitely. The per-client tab paginates by
            // jumping offsets, so accumulating there would stack every visited page into one
            // list — replace instead whenever the query names a client.
            if (args?.clientID) {
              return incoming;
            }

            const merged = existing?.items ? existing.items.slice(0) : [];
            const offset = (args?.offset as number) ?? 0;
            const incomingItems = incoming?.items ?? [];

            for (let i = 0; i < incomingItems.length; i++) {
              merged[offset + i] = incomingItems[i];
            }

            return { ...incoming, items: merged };
          },
        },
        contacts: offsetLimitPagination((args) =>
          Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
        ),
        accounts: offsetLimitPagination((args) =>
          Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
        ),
        Opportunities: offsetLimitPagination((args) =>
          Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
        ),
        Proposals: offsetLimitPagination((args) =>
          Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
        ),
        Programs: offsetLimitPagination((args) =>
          Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
        ),
        actionItems: offsetLimitPagination((args) => {
          return Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg));
        }),
        opportunityStats: offsetLimitPagination((args) =>
          Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
        ),
        kgNotifications: offsetLimitPagination((args) =>
          Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
        ),
        llmConversations: offsetLimitPagination((args) =>
          Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
        ),
        workHistories: offsetLimitPagination((args) =>
          Object.keys(args || {}).filter((arg) => !['offset', 'limit'].includes(arg)),
        ),
      },
    },
  },
});

// --- Apollo Links.

const httpLink = new HttpLink({
  uri: '/query',
});

const batchHttpLink = new BatchHttpLink({
  uri: '/query',
  batchInterval: 5,
});

const persistedQueriesLink = createPersistedQueryLink({ sha256: hash });

export const simpleLink = ApolloLink.from([errorLink, httpLink]);
export const baseLink = ApolloLink.from([errorLink, batchHttpLink]);
export const persistedLink = persistedQueriesLink.concat(baseLink);

// --- Clients.

const clientAwareness = {
  name: 'pv3-uikit',
};

export const simpleClient = new ApolloClient({
  cache: cache,
  link: simpleLink,
  clientAwareness,
});

export const graphClient = new ApolloClient({
  cache: cache,
  link: baseLink,
  clientAwareness,
});

export const graphClientPersisted = new ApolloClient({
  cache: cache,
  link: persistedLink,
  devtools: { enabled: true },
  clientAwareness,
});

export default graphClient;
